/**
 * Product master PURE rules (B0.2' extraction from
 * `mocks/handlers/product.ts` — S1.2 schema readiness rewrite).
 *
 * Moved verbatim: SKU dedupe, PRD-xxxxxx sequencing (list injected),
 * the exactly-one-primary vendor-line invariant, the TEGO H51 price-pair
 * lifecycle (`applyVendorPriceChange`), OEM duplicate detection (S7.4 —
 * advisory, never a gate), read-side `enrichProduct` (breadcrumb + registry
 * readiness + legacy mirrors, categories/lines injected) and the canonical
 * create/patch field application (C108 system-computed `ageGroup`). The live
 * stores, the store-bound `getProductReady`/`selectSalesStatsForProduct`
 * reads and the MSW routes stay in `mocks/handlers/product.ts` — see
 * packages/domain/EXTRACTION-CENSUS.md.
 */

import type {
  Product,
  ProductVendorLine,
  UpdateProductRequest,
} from '@/types/api/product';
import type { ProductCategory } from '@/types/api/productCategory';
import { computeProductReadiness } from '../config/productFieldRegistry';
import { computeAgeGroup } from '../products/computeAgeGroup';

/** Case-insensitive, trimmed SKU dup-guard; `excludeId` for edit mode. */
export function findSkuConflict(
  list: Product[],
  sku: string,
  excludeId?: string
): Product | undefined {
  const needle = sku.trim().toLowerCase();
  if (!needle) return undefined;
  return list.find((p) => p.id !== excludeId && p.sku.trim().toLowerCase() === needle);
}

/** Sequential PRD-xxxxxx assignment (system identity — never enterable). */
export function nextInternalProductId(list: Product[]): string {
  let max = 0;
  for (const p of list) {
    const match = /^PRD-(\d{6})$/.exec(p.internalProductId);
    if (match) max = Math.max(max, parseInt(match[1], 10));
  }
  return `PRD-${String(max + 1).padStart(6, '0')}`;
}

/** Number of primary lines a product currently has (invariant: ≤ 1). */
export function primaryVendorCount(lines: ProductVendorLine[], productId: string): number {
  return lines.filter((l) => l.productId === productId && l.isPrimary).length;
}

/**
 * Marks `vendorId` as THE primary line for `productId` and clears every
 * sibling — the exactly-one-primary invariant (isPreferred mirrors isPrimary).
 */
export function applyPrimaryVendorLine(
  lines: ProductVendorLine[],
  productId: string,
  vendorId: number
): ProductVendorLine[] {
  return lines.map((line) => {
    if (line.productId !== productId) return line;
    const isPrimary = line.vendorId === vendorId;
    return { ...line, isPrimary, isPreferred: isPrimary };
  });
}

// ---------------------------------------------------------------------------
// TEGO H51 vendor price-pair mechanic (SR1.2, US-285) — pure, exported
// ---------------------------------------------------------------------------

/** The 上次採購價 / 下次採購價 pair a vendor line carries (H51). */
export interface VendorPricePair {
  lastPurchasePrice: number | null;
  nextPurchasePrice: number | null;
}

/**
 * The three H51 price-pair lifecycle events:
 * - `quote`      — a (re)negotiated price edits 下次採購價 ONLY; 上次採購價 is
 *   left alone (last === next normally, they diverge only across a change).
 * - `receipt`    — a goods-receipt post rolls 上次採購價 := the transacted
 *   price; 下次採購價 is the standing next-PO price (they re-converge when the
 *   receipt price equals `next`).
 * - `vendorSwitch` — switching the primary vendor sets 上次採購價 = the
 *   outgoing vendor's price and 下次採購價 = the incoming vendor's next-PO price.
 */
export type VendorPriceEvent =
  | { type: 'quote'; price: number }
  | { type: 'receipt'; transactedPrice: number }
  | { type: 'vendorSwitch'; outgoingPrice: number | null; incomingPrice: number };

/**
 * Pure H51 transition — returns a NEW pair, never mutates. Exported for vitest
 * (the handler-as-spec pattern) and reused by the GR-post roll in
 * `goodsReceipt.ts` and PO prefill.
 */
export function applyVendorPriceChange(
  pair: VendorPricePair,
  event: VendorPriceEvent
): VendorPricePair {
  switch (event.type) {
    case 'quote':
      return { lastPurchasePrice: pair.lastPurchasePrice, nextPurchasePrice: event.price };
    case 'receipt':
      return { lastPurchasePrice: event.transactedPrice, nextPurchasePrice: pair.nextPurchasePrice };
    case 'vendorSwitch':
      return { lastPurchasePrice: event.outgoingPrice, nextPurchasePrice: event.incomingPrice };
  }
}

const round = (value: number, dp: number): number => {
  const f = 10 ** dp;
  return Math.round(value * f) / f;
};

export function categoryChain(nodes: ProductCategory[], categoryId: string): ProductCategory[] {
  const target = nodes.find((n) => n.id === categoryId);
  if (!target) return [];
  const byId = new Map(nodes.map((n) => [n.id, n]));
  return target.path
    .split('/')
    .map((id) => byId.get(id))
    .filter((n): n is ProductCategory => Boolean(n));
}

// ---------------------------------------------------------------------------
// OEM duplicate detection (S7.4) — pure, exported for vitest
// ---------------------------------------------------------------------------

/**
 * One same-大類 OEM match.
 *
 * FOLLOW-UP DISCHARGED 2026-08-10: the app-side twin was hoisted into
 * `src/types/api/product.ts` (`CheckOemMatch`) and the read now rides
 * `productApi.checkOem`, so `ProductFormWizard.tsx` aliases that type instead
 * of hand-copying this one. This declaration stays because `packages/domain`
 * must not import from `src/` — the rule owns its own shape, and the
 * oemDupCheck vitest is what keeps the two in step across that boundary.
 */
export interface CheckOemMatch {
  id: string;
  internalProductId: string;
  sku: string;
  displayName: string;
  subsidiaryId: string;
  oemNo: string;
  scn: string | null;
  /** Board scope notes: same 國家/市場 ⇒ 100% 相同商品; cross-market ⇒ 99%/1%. */
  sameMarket: boolean;
}

export interface CheckOemResponse {
  matches: CheckOemMatch[];
}

/** 大類 (product major category) = the root of the materialized path chain. */
function majorCategoryId(categories: ProductCategory[], categoryId: string): string | undefined {
  return categoryChain(categories, categoryId)[0]?.id;
}

/**
 * Board duplicate criteria — BOTH required:「判斷標準有2個：1. 同產品大類
 * 2. 同OEM NUMBER」, searched across 所有國家/市場. OEM matching is
 * case-insensitive on the trimmed number; an incomplete key (no OEM or no
 * category) yields no matches — the check is ADVISORY and never a gate.
 */
export function findOemMatches(
  list: Product[],
  categories: ProductCategory[],
  params: { oemNo: string; categoryId?: string; subsidiaryId?: string; excludeId?: string }
): CheckOemMatch[] {
  const needle = params.oemNo.trim().toLowerCase();
  if (!needle || !params.categoryId) return [];
  const major = majorCategoryId(categories, params.categoryId);
  if (!major) return [];
  return list
    .filter(
      (p) =>
        p.id !== params.excludeId &&
        (p.oemNo ?? '').trim().toLowerCase() === needle &&
        majorCategoryId(categories, p.categoryId) === major
    )
    .map((p) => ({
      id: p.id,
      internalProductId: p.internalProductId,
      sku: p.sku,
      displayName: p.displayName,
      subsidiaryId: p.subsidiaryId ?? '',
      oemNo: p.oemNo ?? '',
      scn: p.scn ?? null,
      sameMarket: Boolean(params.subsidiaryId) && p.subsidiaryId === params.subsidiaryId,
    }));
}

/**
 * Read-side enrichment: derived breadcrumb, readiness (registry-driven) and
 * every deprecated legacy mirror — all recomputed, never stored.
 */
export function enrichProduct(
  p: Product,
  categories: ProductCategory[],
  lines: ProductVendorLine[]
): Product {
  const productLines = lines.filter((v) => v.productId === p.id);
  const primary = productLines.find((v) => v.isPrimary);
  const chain = categoryChain(categories, p.categoryId);
  const categoryPath = chain.map((n) => `${n.nameZh} ${n.nameEn}`).join(' / ');
  const cbm =
    p.lengthMm != null && p.widthMm != null && p.heightMm != null
      ? round((p.lengthMm * p.widthMm * p.heightMm) / 1_000_000_000, 6)
      : undefined;

  const enriched: Product = {
    ...p,
    categoryPath: categoryPath || undefined,
    cubicFeet: cbm != null ? round(cbm * 35.3147, 4) : undefined,
    vendorLines: productLines,
    // Legacy mirrors (deprecated — die with the S1.3/S1.4/S2.1 rebuilds)
    name: p.displayName,
    customerDescription: p.salesDescription,
    vendorDescription: p.purchaseDescription,
    oemPartNumber: p.oemNo ?? null,
    manufacturerPartNumber: p.mpn ?? null,
    baseUom: p.stockUnit,
    purchaseUom: p.purchaseUnit,
    salesUom: p.saleUnit,
    lengthCm: p.lengthMm != null ? round(p.lengthMm / 10, 2) : undefined,
    widthCm: p.widthMm != null ? round(p.widthMm / 10, 2) : undefined,
    heightCm: p.heightMm != null ? round(p.heightMm / 10, 2) : undefined,
    grossWeightKg: p.weightKg,
    cbm,
    preferredVendorId: primary?.vendorId ?? null,
    preferredVendorName: primary?.vendorName ?? null,
    listPrice: p.basePrice ?? undefined,
  };

  const readiness = computeProductReadiness(enriched);
  enriched.readiness = readiness;
  enriched.occStatus = readiness.ready ? 'ready' : 'blocked';
  enriched.occBlockedFields = [...readiness.missing];
  return enriched;
}

/**
 * Canonical MUTABLE field subset applied on PATCH (legacy keys ignored).
 * `asOfYear` feeds the C108 `ageGroup` recompute — optional so existing
 * callers/tests keep their shape, pinnable for determinism.
 *
 * NEITHER `subsidiaryId` NOR `unitsTypeId` (D-4 + D-5, 2026-08-10), and no
 * `isActive` (D-3). This function is the MOCK's PATCH — the executable spec —
 * and it used to apply all three, none of which `ProductUpdateInput` can
 * carry. That is not a mock being generous: it is the spec disagreeing with
 * the server about what a save does, which is precisely what let two lost
 * writes look correct in every mock-backed test while Postgres never moved.
 * The mock's CREATE still sets both (mocks/handlers/product.ts builds the row
 * explicitly and is the only other caller) — create-only means create-only,
 * not never-written.
 */
export function applyCanonicalFields(
  target: Product,
  body: UpdateProductRequest,
  asOfYear: number = new Date().getFullYear()
): Product {
  const next: Product = { ...target };
  if (body.displayName !== undefined) next.displayName = body.displayName.trim();
  if (body.includeChildren !== undefined) next.includeChildren = body.includeChildren;
  if (body.categoryId !== undefined) next.categoryId = body.categoryId;
  if (body.secondaryCategoryIds !== undefined)
    next.secondaryCategoryIds = body.secondaryCategoryIds;
  if (body.stockUnit !== undefined) next.stockUnit = body.stockUnit;
  if (body.purchaseUnit !== undefined) next.purchaseUnit = body.purchaseUnit;
  if (body.saleUnit !== undefined) next.saleUnit = body.saleUnit;
  if (body.salesDescription !== undefined) next.salesDescription = body.salesDescription;
  if (body.purchaseDescription !== undefined) next.purchaseDescription = body.purchaseDescription;
  if (body.customsDescription !== undefined) next.customsDescription = body.customsDescription;
  if (body.purchaseOrderNote !== undefined) next.purchaseOrderNote = body.purchaseOrderNote; // DD!46
  if (body.oemNo !== undefined) next.oemNo = body.oemNo;
  if (body.mpn !== undefined) next.mpn = body.mpn;
  if (body.upcCode !== undefined) next.upcCode = body.upcCode;
  if (body.customerPartNumbers !== undefined) next.customerPartNumbers = body.customerPartNumbers;
  if (body.sparePartNumbers !== undefined) next.sparePartNumbers = body.sparePartNumbers; // 備件料號

  if (body.images !== undefined) next.images = body.images.slice(0, 5);
  if (body.fitment !== undefined) next.fitment = body.fitment;
  if (body.variant !== undefined) next.variant = body.variant; // DD!31
  if (body.extractYear !== undefined) next.extractYear = body.extractYear; // DD!32
  if (body.manufacturer !== undefined) next.manufacturer = body.manufacturer;
  if (body.countryOfManufacture !== undefined)
    next.countryOfManufacture = body.countryOfManufacture;
  if (body.faSubCategory !== undefined) next.faSubCategory = body.faSubCategory; // DD!69
  if (body.ageStartYear !== undefined) next.ageStartYear = body.ageStartYear;
  if (body.ageEndYear !== undefined) next.ageEndYear = body.ageEndYear;
  // C108 【MUST】 — `ageGroup` is SYSTEM-COMPUTED from the year range; a
  // caller-sent `ageGroup` is deliberately IGNORED (see CreateProductRequest).
  next.ageGroup = computeAgeGroup(next.ageStartYear, next.ageEndYear, asOfYear);
  if (body.isShared !== undefined) next.isShared = body.isShared; // DD!19 共用
  if (body.productNotes !== undefined) next.productNotes = body.productNotes;
  if (body.damageStockNotes !== undefined) next.damageStockNotes = body.damageStockNotes;
  if (body.itemComment !== undefined) next.itemComment = body.itemComment;
  if (body.lengthMm !== undefined) next.lengthMm = body.lengthMm;
  if (body.widthMm !== undefined) next.widthMm = body.widthMm;
  if (body.heightMm !== undefined) next.heightMm = body.heightMm;
  if (body.weightKg !== undefined) next.weightKg = body.weightKg;
  if (body.weightUnit !== undefined) next.weightUnit = body.weightUnit;
  if (body.costingMethod !== undefined) next.costingMethod = body.costingMethod;
  // D17 per-product anchor — null clears back to follow the company default.
  if (body.pricingAnchor !== undefined) next.pricingAnchor = body.pricingAnchor ?? undefined;
  if (body.basePrice !== undefined) next.basePrice = body.basePrice;
  if (body.currency !== undefined) next.currency = body.currency;
  if (body.minQty !== undefined) next.minQty = body.minQty ?? undefined;
  if (body.minQtyUnit !== undefined) next.minQtyUnit = body.minQtyUnit ?? undefined; // DD!100
  if (body.maxQty !== undefined) next.maxQty = body.maxQty ?? undefined;
  if (body.maxQtyUnit !== undefined) next.maxQtyUnit = body.maxQtyUnit ?? undefined; // DD!102
  if (body.itemDefinedCost !== undefined) next.itemDefinedCost = body.itemDefinedCost ?? undefined;
  if (body.itemDefinedCostUnit !== undefined)
    next.itemDefinedCostUnit = body.itemDefinedCostUnit ?? undefined; // DD!98
  if (body.accounting !== undefined) next.accounting = body.accounting;
  // SR0.2 — replenishment planning passthrough (SHOULD-tier; never gates).
  if (body.planning !== undefined) next.planning = body.planning;
  if (body.nameZh !== undefined) next.nameZh = body.nameZh;
  return next;
}
